import * as yup from 'yup'

// utils
import { 
    PAN_REGEX_FORMAT, 
    EMAIL_REGEX_FORMAT,
    trimmedInputValues,
    capitalizeWordsInValue,
} from '../utils';

const companyDetailSchema = yup.object().shape({
    partialOnboarding: yup.boolean().required('Partial Onboarding is required'),
    companyName: yup.string()
        .transform((value) => capitalizeWordsInValue(value))
        .required('Please enter company name')
        .transform((value) => trimmedInputValues(value)),
    groupId: yup.object().when("isInternational", {
        is: true,
        then: () => yup.object().nullable(),
        otherwise: () => yup.object().when("buyerSeller", {
            is: 2, 
            then: () => yup.object().nullable(),
            otherwise: () => yup.object().required("Please select company group"),
        }),
    }),
    buyerSeller: yup.number().required("Buyer/Seller detail is required"),
    clientSuccessManagerId: yup.object().when("isInternational", {
        is: true,
        then: () => yup.object().transform((value) => null).nullable(),
        otherwise: () => yup.object().when("buyerSeller", {
          is: 2,
          then: () => yup.object().transform((value) => null).nullable(),
          otherwise: () => yup.object().required("Please select client success manager"),
        }),
    }),      
    address: yup.string()
        .transform((value) => trimmedInputValues(value))
        .required('Please enter address'),
    countryId: yup.number().required('Please enter the country'),
    stateId: yup.number().required('Please select state'),
    city: yup.string()
        .transform((value) => capitalizeWordsInValue(value))
        .transform((value) => trimmedInputValues(value))
        .required('Please enter city'),
    pinCode: yup.string()
        .transform((value) => trimmedInputValues(value))
        .required('Please enter the pincode')
        .test('pincodeLength', function(value) {
          const pmCompanyConfig = this.options.context
          
          const pinCodeLength = pmCompanyConfig.pinCodeLength
          const pinCodeErrorMessage = `Must be exactly ${pinCodeLength} characters`;
          
          if (value && value.length !== pinCodeLength) {
            return this.createError({ message: pinCodeErrorMessage })
          }
          return true
        }),
    panCode: yup.string().when("isInternational", {
        is: true,
        then: () => yup.string().nullable(),
        otherwise: () => yup.string().when("partialOnboarding", {
          is: true,
          then: () => yup.string().when(["panCode"], {
            is: (panCode) => panCode != '' ,
            then: () => yup.string()
                .transform((value) => trimmedInputValues(value))
                .matches(PAN_REGEX_FORMAT, 'Please enter valid PAN no')
                .test('len', 'PAN Must be exactly 10 characters', value => value && value.length === 10)
                .required('Please enter pan-no'),
            otherwise: () => yup.string().nullable()
            }),
            otherwise: () => yup.string()
                .transform((value) => trimmedInputValues(value))
                .matches(PAN_REGEX_FORMAT, 'Please enter valid PAN no')
                .test('len', 'PAN must be exactly 10 characters', value => value.length === 10)
                .required('Please enter pan-no'),
        }),
    }),
    /*gstinCode: yup.string().required('Please enter gstinCode'),*/
    gstinCode: yup.string().when(["partialOnboarding", "isInternational"], {
      is: (partialOnboarding, isInternational) => partialOnboarding || isInternational,
      then: () => yup.string().nullable(),
      otherwise: () => yup.string().when('pmCompanyConfig', (pmCompanyConfig, schema) => {
        const addressErrorMessage = pmCompanyConfig?.[0] ? `Please enter ${pmCompanyConfig[0].GSTINLabel}` : "Please enter GSTIN/NPWP/TIN"
        return schema.required(addressErrorMessage)
      }),
    }),
    supplyTypeId: yup.number().when("isInternational", {
        is: true,
        then: () => yup.number().nullable(),
        otherwise: () => yup.number().when("buyerSeller", {
          is: 2,
          then: () => yup.number()
            .transform((value) => null)
            .nullable(),
          otherwise: () => yup.number().when("partialOnboarding", {
            is: false,
            then: () => yup.number().required("Please select supply-type"),
            otherwise: () => yup.number().nullable(),
          }),
        }),
    }),
    ownershipTypeId: yup.number().when("isInternational", {
        is: true,
        then: () => yup.number().nullable(),
        otherwise: () => yup.number().when("buyerSeller", {
          is: 1,
          then: () => yup.number()
            .transform((value) => null)
            .nullable(),
          otherwise: () => yup.number().when("partialOnboarding", {
            is: false,
            then: () => yup.number().required("Please select ownership type"),
            otherwise: () => yup.number().nullable(),
          }),
        }),
    }),      
    sizeId: yup.number().when("isInternational", {
        is: true,
        then: () => yup.number().nullable(),
        otherwise: () => yup.number().when("buyerSeller", {
          is: 1,
          then: () => yup.number()
            .transform((value) => null)
            .nullable(),
          otherwise: () => yup.number().when("partialOnboarding", {
            is: false,
            then: () => yup.number().required("Please select company size"),
            otherwise: () => yup.number().nullable(),
          }),
        }),
    }),      
    businessTypeId: yup.number().when("isInternational", {
        is: true,
        then: () => yup.number().nullable(),
        otherwise: () => yup.number().when("buyerSeller", {
          is: 1,
          then: () => yup.number()
            .transform((value) => null)
            .nullable(),
          otherwise: () => yup.number().when("partialOnboarding", {
            is: false,
            then: () => yup.number().required("Please select business type"),
            otherwise: () => yup.number().nullable(),
          }),
        }),
    }),      
    turnoverCategoryId: yup.number().when("isInternational", {
        is: true,
        then: () => yup.number().nullable(),
        otherwise: () => yup.number().when("buyerSeller", {
          is: 1,
          then: () => yup.number()
            .transform((value) => null)
            .nullable(),
          otherwise: () => yup.number().when("partialOnboarding", {
            is: false,
            then: () => yup.number().required("Please enter annual turnover detail"),
            otherwise: () => yup.number().nullable(),
          }),
        }),
    }),      
    prefPMBranchId: yup.number().required("Please select Pref PM Branch"),
    tdsSection194q: yup.boolean().when("isInternational", {
        is: true,
        then: () => yup.boolean(),
        otherwise: () => yup.boolean().when("buyerSeller", {
          is: 1,
          then: () => yup.boolean(),
          otherwise: () => yup.boolean().when("partialOnboarding", {
            is: false,
            then: () => yup.boolean().required("Please select tds section type"),
            otherwise: () => yup.boolean(),
          }),
        }),
    }),  
    tdsSectionId: yup.number().when("isInternational", {
        is: true,
        then: () => yup.number().nullable(),
        otherwise: () => yup.number().when("buyerSeller", {
          is: 1,
          then: () => yup.number()
            .transform((value) => null)
            .nullable(),
          otherwise: () => yup.number().when("enableServicePurchase", {
            is: true,
            then: () => yup.number().required("Please select TDS Section"),
            otherwise: () => yup.number().nullable(),
          }),
        }),
    }),
    isMSMEDRegd: yup.string().when("isInternational", {
        is: true,
        then: () => yup.string().nullable(),
        otherwise: () => yup.string().when("buyerSeller", {
          is: 1,
          then: () => yup.string().nullable(),
          otherwise: () => yup.string().when("partialOnboarding", {
            is: false,
            then: () => yup.string().required("Please select msmmed"),
            otherwise: () => yup.string().nullable(),
          }),
        }),
    }),     
    companyMSMEDTypeId: yup.number().when("isInternational", {
      is: true,
      then: () => yup.number().nullable(),
      otherwise: () => yup.number().when("buyerSeller", {
        is: 1,
        then: () => yup.number()
          .transform((value) => null)
          .nullable(),
        otherwise: () => yup.number().when(["isMSMEDRegd"], {
          is: (isMSMEDRegd) => isMSMEDRegd === "Registered",
          then: () => yup.number().required("Please select reg msmmed type"),
          otherwise: () => yup.number().nullable(),
        }),
      }),
    }),          
    hodFirstName: yup.string()
        .transform((value) => trimmedInputValues(value))
        .required('Please enter hod first name'),
    hodLastName: yup.string()
        .transform((value) => trimmedInputValues(value))
        .required('Please enter hod last name'),
    hodISDCode: yup.string()
        .required('Please select ISD Code'),
    hodPhoneNo: yup.string()
        .required('Please enter hod phone no')
        .test('len', function(value) {
          const pmCompanyConfig = this.options.context

          const phoneNumberLength = pmCompanyConfig.phoneNumberLength
          const pinCodeErrorMessage = `Please enter valid phone number`

          if (value && !phoneNumberLength.includes(value.length)) {
            return this.createError({ message: pinCodeErrorMessage })
          }
          return true
        }),
    hodEmail: yup.string()
        .transform((value) => trimmedInputValues(value))
        .lowercase()
        .matches(EMAIL_REGEX_FORMAT, 'Please enter valid email')
        .required('Please enter hod email'),
    companyUsers: yup.array().of(
        yup.object().shape({
            firstName: yup.string()
                .transform((value) => trimmedInputValues(value))
                .required('Please enter user first name'),
            lastName: yup.string()
                .transform((value) => trimmedInputValues(value))
                .required('Please enter user last name'),
            ISDCode: yup.string()
                .required('Please select ISD Code'),
            phoneNumber: yup.string()
                .transform((value) => trimmedInputValues(value))
                .required('Please enter user phone no')
                .test('len', function(value) {
                  const pmCompanyConfig = this.options.context
        
                  const phoneNumberLength = pmCompanyConfig.phoneNumberLength
                  const pinCodeErrorMessage = `Please enter valid phone number`
        
                  if (value && !phoneNumberLength.includes(value.length)) {
                    return this.createError({ message: pinCodeErrorMessage })
                  }
                  return true
                }),
            emailId: yup.string()
                .transform((value) => trimmedInputValues(value))
                .lowercase()
                .matches(EMAIL_REGEX_FORMAT, 'Please enter valid email')
                .required('Please enter user email'),
            companyRoleId: yup.string().required('Please select user role'),
        })
    ),
    companyRoles: yup.array().of(
        yup.object().shape({
            roleName: yup.string().required('Please enter role name'),
        })
    ),
  preferredPaymentMode: yup.string().when("buyerSeller", {
      is: 1,
      then: () => yup.string().nullable(),
      otherwise: () => yup.string().when("partialOnboarding", {
        is: false,
        then: () => yup.string().required("Please select preferred payment mode"),
        otherwise: () => yup.string().nullable(),
      }),
  }),
}, ['gstinCode']);

const initialCompanyDetails = {
    companyName: '',
    webpage: '',
    companyDescription: '',
    companyShortName: '',
    buyerSeller: null,
    partialOnboarding: false,
    address: '',
    countryId: null,
    stateId: null,
    city: '',
    pinCode: '',
    panCode: '',
    gstinCode: '',
    supplyTypeId: null,
    ownershipTypeId: null,
    sizeId: null,
    businessTypeId: null,
    turnoverCategoryId: null,
    tdsSection194q: false,
    isMSMEDRegd: '',
    paymentTerms: {
        payAdvPO: 0,
        payAdvDelivery: 0,
        payPDCDelivery: 0,
        payOnDelivery: 0,
        payCredit: 0,
        creditPeriodId: null,
    },
    hodFirstName: "",
    hodLastName: "",
    hodPhoneNo: "",
    hodEmail: "",
    gstinCert: "",
    aadharCert: "",
    panCert: "",
    msmedCert: "",
    incorporationCert: "",
    preferredPaymentMode: null,
    companyMSMEDTypeId: null
  }

export { companyDetailSchema, initialCompanyDetails }